/**
 * ArticleSettingsDialog — compact article-settings surface for the universal
 * editor (WS-E: slug/URL editing).
 *
 * Two save paths, chosen by publish state:
 *  - UNPUBLISHED (no wordpress_post_id): persists url_slug via the existing
 *    content PATCH (blogCreationService.patchContent — same body shape the
 *    legacy ContentEditorPage auto-save sends). Publish paths prefer
 *    content.url_slug over slugify(keyword) when set.
 *  - PUBLISHED: POST /blog-creation/content/:id/slug via
 *    blogCreationService.updateSlug — the backend changes the live WordPress
 *    slug and creates a 301 redirect from the old URL, so an inline confirm
 *    step warns before anything live changes.
 *
 * The slug input mirrors the SEOMetadataPanel pattern (auto-slugify on
 * change); that component isn't reused directly because its props require
 * meta title/description state this drawer doesn't own.
 */

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { blogCreationService } from '@/services/blogCreationService';

/** Keystroke-level slugify — same rules as SEOMetadataPanel's slug input. */
const slugifyInput = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-');

/** Final normalization before save: also trim leading/trailing hyphens. */
const normalizeSlug = (value: string) =>
  slugifyInput(value).replace(/^-+|-+$/g, '');

/**
 * Best-effort slug from a stored wordpress_post_url (pretty permalinks only —
 * `?p=ID` query-form URLs carry no slug and yield '').
 */
function deriveSlugFromUrl(url: string | undefined): string {
  if (!url) return '';
  try {
    const segments = new URL(url).pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] ?? '';
  } catch {
    return '';
  }
}

export interface ArticleSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentId: string;
  /** Stored blog_content.url_slug (may be empty for older content). */
  initialSlug: string;
  wordpressPostId?: number;
  wordpressPostUrl?: string;
  /** Called after a successful save — the page passes refetchContent. */
  onSaved?: () => Promise<unknown> | unknown;
}

export function ArticleSettingsDialog({
  open,
  onOpenChange,
  contentId,
  initialSlug,
  wordpressPostId,
  wordpressPostUrl,
  onSaved,
}: ArticleSettingsDialogProps) {
  const isPublished = Boolean(wordpressPostId);
  const startingSlug = initialSlug || deriveSlugFromUrl(wordpressPostUrl);

  const [slug, setSlug] = useState(startingSlug);
  const [confirming, setConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Re-sync the draft each time the dialog opens (content may have refetched
  // since the last edit).
  useEffect(() => {
    if (open) {
      setSlug(startingSlug);
      setConfirming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanSlug = normalizeSlug(slug);

  const persistDraftSlug = useCallback(async () => {
    setIsSaving(true);
    try {
      await blogCreationService.patchContent(contentId, {
        url_slug: cleanSlug,
      });
      toast.success('Slug saved — it will be used when this article publishes');
      await onSaved?.();
      onOpenChange(false);
    } catch (err) {
      console.error('[ArticleSettingsDialog] slug save failed:', err);
      toast.error('Failed to save slug');
    } finally {
      setIsSaving(false);
    }
  }, [cleanSlug, contentId, onOpenChange, onSaved]);

  const applyLiveSlugChange = useCallback(async () => {
    setIsSaving(true);
    try {
      const result = await blogCreationService.updateSlug(contentId, cleanSlug);
      toast.success(`URL updated to ${result.new_url}`, {
        description: result.redirect_created
          ? 'A 301 redirect from the old URL was created.'
          : 'No redirect was created for the old URL.',
        ...(result.new_url && {
          action: {
            label: 'View post',
            onClick: () =>
              window.open(result.new_url, '_blank', 'noopener,noreferrer'),
          },
        }),
      });
      await onSaved?.();
      onOpenChange(false);
    } catch (err) {
      console.error('[ArticleSettingsDialog] live slug update failed:', err);
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Failed to update the URL',
      );
      setConfirming(false);
    } finally {
      setIsSaving(false);
    }
  }, [cleanSlug, contentId, onOpenChange, onSaved]);

  const handleSaveClick = useCallback(() => {
    if (!cleanSlug) {
      toast.error('Slug cannot be empty');
      return;
    }
    if (isPublished) {
      // Live URL change — require an explicit confirmation first.
      setConfirming(true);
      return;
    }
    void persistDraftSlug();
  }, [cleanSlug, isPublished, persistDraftSlug]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Article settings</DialogTitle>
          <DialogDescription>
            {isPublished
              ? 'Change the URL slug of the published article.'
              : 'Set the URL slug used when this article is published.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {wordpressPostUrl && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                Current URL
              </Label>
              <a
                href={wordpressPostUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-teal-dark hover:underline break-all"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                {wordpressPostUrl}
              </a>
            </div>
          )}

          {/* URL slug — same input pattern as SEOMetadataPanel */}
          <div className="space-y-1.5">
            <Label htmlFor="article-url-slug" className="text-xs text-muted-foreground">
              URL Slug
            </Label>
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-muted-foreground">/</span>
              <Input
                id="article-url-slug"
                value={slug}
                onChange={(e) => setSlug(slugifyInput(e.target.value))}
                placeholder="your-page-url"
                className="text-sm"
                disabled={isSaving || confirming}
              />
            </div>
          </div>

          {confirming && (
            <div className="rounded-md bg-coral-light p-3 text-sm text-coral-dark">
              This changes the live URL — a 301 redirect from the old URL will
              be created.
            </div>
          )}
        </div>

        <DialogFooter>
          {confirming ? (
            <>
              <Button
                variant="outline"
                onClick={() => setConfirming(false)}
                disabled={isSaving}
              >
                Back
              </Button>
              <Button
                className="bg-coral text-white hover:bg-coral-hover"
                onClick={() => void applyLiveSlugChange()}
                disabled={isSaving}
              >
                {isSaving ? 'Updating…' : 'Confirm & update URL'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                className="bg-coral text-white hover:bg-coral-hover"
                onClick={handleSaveClick}
                disabled={isSaving || !cleanSlug}
              >
                {isSaving
                  ? 'Saving…'
                  : isPublished
                    ? 'Update URL'
                    : 'Save slug'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
